<template>
  <div class="customer-quarterly-audit" v-loading="loading">
    <el-card shadow="never" class="summary-card">
      <div slot="header">
        上季度容量审核
        <span v-if="period.from && period.to" class="period">
          （{{ period.from }} 至 {{ period.to }}）
        </span>
      </div>
      <el-alert
        type="info"
        :closable="false"
        show-icon
        title="仅当成员容量已达上限、上季度人均产出低于团队平均且存在可释放客户时，队长才可释放该成员当前持有客户中上季度业绩末位 20%。"
      />
    </el-card>

    <el-card shadow="never">
      <el-table :data="rows" size="small" empty-text="暂无可审核成员">
        <el-table-column prop="team_name" label="团队" min-width="130" />
        <el-table-column prop="user_name" label="成员" min-width="110" />
        <el-table-column label="客户/容量" min-width="100">
          <template slot-scope="{ row }">{{ row.customer_count || 0 }} / {{ row.capacity_limit || 0 }}</template>
        </el-table-column>
        <el-table-column label="容量占比" min-width="100">
          <template slot-scope="{ row }">{{ toPercent(row.capacity_ratio) }}</template>
        </el-table-column>
        <el-table-column label="上季度业绩" min-width="110" align="right">
          <template slot-scope="{ row }">{{ formatAmount(row.quarter_amount) }}</template>
        </el-table-column>
        <el-table-column label="人均产出" min-width="110" align="right">
          <template slot-scope="{ row }">{{ formatAmount(row.productivity_amount) }}</template>
        </el-table-column>
        <el-table-column label="团队平均" min-width="110" align="right">
          <template slot-scope="{ row }">{{ formatAmount(row.team_avg_productivity_amount) }}</template>
        </el-table-column>
        <el-table-column prop="releasable_customer_count" label="可释放客户数" min-width="120" align="right" />
        <el-table-column label="审核结论" min-width="150">
          <template slot-scope="{ row }">
            <el-tag v-if="row.eligible_for_release" type="danger" size="mini">可释放</el-tag>
            <span v-else class="disabled-reason">{{ row.release_disabled_reason || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="hasReleaseAction" label="操作" width="130" fixed="right">
          <template slot-scope="{ row }">
            <el-tooltip :disabled="row.eligible_for_release" :content="row.release_disabled_reason || '当前不可释放'" placement="top">
              <span>
                <el-button
                  type="danger"
                  size="mini"
                  :disabled="!row.eligible_for_release"
                  @click="openPreview(row)"
                >预览释放</el-button>
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      title="释放客户预览"
      :visible.sync="previewVisible"
      width="760px"
      append-to-body
    >
      <div v-loading="previewLoading">
        <template v-if="preview.member">
          <el-alert
            class="preview-alert"
            type="warning"
            :closable="false"
            show-icon
            :title="`将释放「${preview.member.user_name}」的 ${previewCustomers.length} 个低产出客户，确认后客户将进入公海。`"
          />
          <el-table :data="previewCustomers" size="small" max-height="360" empty-text="暂无可释放客户">
            <el-table-column prop="name" label="客户" min-width="160" />
            <el-table-column label="上季度业绩" width="120" align="right">
              <template slot-scope="{ row }">{{ formatAmount(row.quarter_amount) }}</template>
            </el-table-column>
            <el-table-column label="最近有效跟进" width="150">
              <template slot-scope="{ row }">{{ formatDateTime(row.last_followed_at) }}</template>
            </el-table-column>
            <el-table-column label="最近成交" width="120">
              <template slot-scope="{ row }">{{ formatDate(row.last_ordered_on) }}</template>
            </el-table-column>
            <el-table-column prop="pool_risk_note" label="说明" min-width="120" />
          </el-table>
        </template>
      </div>
      <div slot="footer">
        <el-button @click="previewVisible = false">取消</el-button>
        <el-button type="danger" :disabled="previewLoading || !previewCustomers.length" @click="confirmRelease">确认释放</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { request } from 'beans-admin-plugin';
import moment from 'moment';

@Component
export default class CustomerQuarterlyAudit extends Vue {
  loading = false;
  previewLoading = false;
  previewVisible = false;
  rows = [];
  period = {};
  currentRow = null;
  preview = {};

  mounted() {
    this.fetchData();
  }

  get hasReleaseAction() {
    return this.rows.some(row => row.can_release);
  }

  get previewCustomers() {
    return this.preview.customers || [];
  }

  toPercent(value) {
    return `${((value || 0) * 100).toFixed(1)}%`;
  }

  formatAmount(value) {
    return Number(value || 0).toFixed(2);
  }

  formatDate(value) {
    return value ? moment(value).format('YYYY-MM-DD') : '-';
  }

  formatDateTime(value) {
    return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-';
  }

  async fetchData() {
    this.loading = true;
    try {
      const result = await request.get('/dashboard/customer_quarterly_audit');
      this.rows = result.rows || [];
      this.period = result.period || {};
    } finally {
      this.loading = false;
    }
  }

  async openPreview(row) {
    this.currentRow = row;
    this.preview = {};
    this.previewVisible = true;
    this.previewLoading = true;
    try {
      this.preview = await request.get('/dashboard/customer_quarterly_audit_preview', { user_id: row.user_id }) || {};
    } finally {
      this.previewLoading = false;
    }
  }

  async confirmRelease() {
    if (!this.currentRow) return;

    try {
      await this.$confirm(
        `确认释放「${this.currentRow.user_name}」的 ${this.previewCustomers.length} 个低产出客户到公海？`,
        '确认释放',
        { type: 'warning' }
      );
    } catch (error) {
      return;
    }

    this.loading = true;
    try {
      const result = await request.put('/dashboard/release_customer_quarterly_audit', { user_id: this.currentRow.user_id });
      this.$message.success(`已释放 ${result.released_count || 0} 个客户到公海`);
      this.previewVisible = false;
      await this.fetchData();
    } finally {
      this.loading = false;
    }
  }
}

export const route = [
  {
    path: '/customer_quarterly_audit',
    name: 'customer_quarterly_audit.index',
    component: CustomerQuarterlyAudit,
  },
];
</script>

<style scoped>
.customer-quarterly-audit {
  padding: 16px;
}
.summary-card {
  margin-bottom: 16px;
}
.period {
  color: #909399;
  font-size: 13px;
  font-weight: normal;
}
.disabled-reason {
  color: #909399;
  font-size: 12px;
}
.preview-alert {
  margin-bottom: 12px;
}
</style>
